import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { 
  Users, CreditCard, Mail, MessageSquare, FileText, BookOpen,
  TrendingUp, DollarSign, CheckCircle, Clock, XCircle, BarChart3
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminHub() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    pendingPayments: 0,
    subscribers: 0,
    newsletterSignups: 0,
    pendingTestimonials: 0,
    resources: 0,
    teachingTips: 0
  });
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    checkAdminAndFetchStats();
  }, []);

  const checkAdminAndFetchStats = async () => {
    try {
      setLoading(true);
      
      // Check admin access first
      const authResponse = await axios.get(`${API}/auth/me`, { withCredentials: true });
      
      if (!authResponse.data) {
        navigate('/login?redirect=/admin');
        return;
      }
      
      if (!authResponse.data.is_admin) {
        // Silently redirect non-admin users without showing error toast
        navigate('/');
        return;
      }
      
      setAuthChecked(true);
      
      // Fetch stats
      const statsResponse = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(statsResponse.data);
      
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/login?redirect=/admin');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const adminSections = [
    {
      title: 'Payment Approvals',
      description: 'Review and approve pending payments',
      icon: CreditCard,
      count: stats.pendingPayments,
      badge: 'Pending',
      badgeColor: 'bg-orange-100 text-orange-700',
      route: '/admin/payments',
      color: 'from-orange-500 to-red-500'
    },
    {
      title: 'Subscribers',
      description: 'Manage active subscriptions',
      icon: Users,
      count: stats.subscribers,
      badge: 'Active',
      badgeColor: 'bg-green-100 text-green-700',
      route: '/admin/subscriptions',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Testimonials',
      description: 'Approve parent & teacher testimonials',
      icon: MessageSquare,
      count: stats.pendingTestimonials,
      badge: 'Pending',
      badgeColor: 'bg-yellow-100 text-yellow-700',
      route: '/admin/testimonials',
      color: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Newsletter',
      description: 'View newsletter subscribers',
      icon: Mail,
      count: stats.newsletterSignups,
      badge: 'Signups',
      badgeColor: 'bg-blue-100 text-blue-700',
      route: '/admin/newsletter',
      color: 'from-indigo-500 to-purple-500'
    },
    {
      title: 'Resources',
      description: 'Manage worksheets, lesson plans & more',
      icon: FileText,
      count: stats.resources,
      badge: 'Published',
      badgeColor: 'bg-slate-100 text-slate-700',
      route: '/admin/resources',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      title: 'Teacher Tips',
      description: 'Manage teaching tips & articles',
      icon: BookOpen,
      count: stats.teachingTips,
      badge: 'Articles',
      badgeColor: 'bg-purple-100 text-purple-700',
      route: '/admin/resources',
      color: 'from-pink-500 to-rose-500'
    },
    {
      title: 'Payment Settings',
      description: 'Update payment details & pricing',
      icon: DollarSign,
      count: null,
      route: '/admin/payment-settings',
      color: 'from-amber-500 to-orange-500'
    },
    {
      title: 'All Users',
      description: 'View all registered users',
      icon: BarChart3,
      count: stats.totalUsers,
      badge: 'Users',
      badgeColor: 'bg-cyan-100 text-cyan-700',
      route: '/admin/users',
      color: 'from-cyan-500 to-blue-500'
    },
    {
      title: 'Usage Analytics',
      description: 'AI generations & credit consumption',
      icon: TrendingUp,
      count: null,
      route: '/admin/usage-analytics',
      color: 'from-purple-500 to-pink-500'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="py-24 px-6 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Loading admin dashboard...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />

      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Admin Dashboard</h1>
            <p className="text-base text-slate-600">
              Manage your platform, approve payments, and review content
            </p>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-8 h-8 text-blue-600" />
                <span className="text-3xl font-bold text-slate-900">{stats.totalUsers}</span>
              </div>
              <p className="text-sm font-semibold text-slate-600">Total Users</p>
            </div>

            <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <span className="text-3xl font-bold text-slate-900">{stats.subscribers}</span>
              </div>
              <p className="text-sm font-semibold text-slate-600">Active Subscribers</p>
            </div>

            <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <Clock className="w-8 h-8 text-orange-600" />
                <span className="text-3xl font-bold text-slate-900">{stats.pendingPayments}</span>
              </div>
              <p className="text-sm font-semibold text-slate-600">Pending Payments</p>
            </div>

            <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-8 h-8 text-purple-600" />
                <span className="text-3xl font-bold text-slate-900">{stats.resources + stats.teachingTips}</span>
              </div>
              <p className="text-sm font-semibold text-slate-600">Total Content</p>
            </div>
          </div>

          {/* Admin Sections Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {adminSections.map((section, index) => {
              const Icon = section.icon;
              return (
                <button
                  key={index}
                  onClick={() => navigate(section.route)}
                  className="bg-white rounded-2xl border-2 border-slate-200 p-6 shadow-sm hover:shadow-lg hover:border-slate-300 transition-all text-left group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`bg-gradient-to-br ${section.color} rounded-xl p-3 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6 text-white" strokeWidth={2} />
                    </div>
                    
                    {section.count !== null && (
                      <div className="flex flex-col items-end">
                        <span className="text-2xl font-bold text-slate-900">{section.count}</span>
                        {section.badge && (
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${section.badgeColor} mt-1`}>
                            {section.badge}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 mb-1 group-hover:text-purple-600 transition-colors">
                    {section.title}
                  </h3>
                  <p className="text-sm text-slate-600">{section.description}</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default AdminHub;
